/**
 * Sortable, filterable table over TanStack Table.
 *
 * Kept thin on purpose: column definitions live with the feature that owns
 * them, so a column can render a `Qualified` value or a confidence badge
 * without this component needing to know anything about data quality.
 */

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useState, type ReactNode } from 'react';
import { useT } from '../../i18n';

export function DataTable<T>({
  data,
  columns,
  initialSorting = [],
  globalFilter,
  emptyMessage,
  onRowClick,
}: {
  data: T[];
  columns: Array<ColumnDef<T, unknown>>;
  initialSorting?: SortingState;
  globalFilter?: string;
  emptyMessage?: ReactNode;
  onRowClick?: (row: T) => void;
}) {
  const t = useT();
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter: globalFilter ?? '' },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Sorting nulls last: "we do not know this player's power" should not win
    // a descending sort over a player whose power we actually measured.
    sortingFns: {},
    enableSortingRemoval: false,
  });

  const rows = table.getRowModel().rows;

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const direction = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    className={canSort ? 'sortable' : undefined}
                    style={{ width: header.column.columnDef.size }}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={
                      direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'
                    }
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {direction ? (direction === 'asc' ? ' ▲' : ' ▼') : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={table.getAllLeafColumns().length} className="muted" style={{ padding: 20 }}>
                {emptyMessage ?? t.common.nothingToShow}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
